import React from "react";
import {
  Close,
  MoreVert,
  Send,
  Preview,
  Visibility,
  Delete,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { SubmissionReviewActivity, SubmissionReviewStatus } from "../../types";
import { Link } from "react-router-dom";
import { SubmissionReviewState } from "./State";
import { CampaignProgress } from "../Campaign/Progress";
import { useTranslation } from "react-i18next";

export interface Props {
  activity: SubmissionReviewActivity;
  onChange: (activity: SubmissionReviewActivity, activityLabel: string) => void;
  onDelete: (activity: SubmissionReviewActivity) => void;
  onRemind: (activity: SubmissionReviewActivity) => void;
}

/**
 * One progress row on the card. Always rendered — a "Not started" placeholder
 * stands in before that stage begins, so every card has the same number of
 * rows and (with the clamped title) the same height.
 */
function ProgressRow({
  label,
  submitted,
  total,
}: {
  label: string;
  submitted: number;
  total: number;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", minHeight: 24 }}>
      <Typography variant="caption" sx={{ mr: 1, minWidth: 80 }}>
        {label}:
      </Typography>
      {total > 0 ? (
        <CampaignProgress submitted={submitted} total={total} />
      ) : (
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      )}
    </Box>
  );
}

export const ActivityCard = ({
  activity,
  onChange,
  onDelete,
  onRemind,
}: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [t] = useTranslation();
  const open = Boolean(anchorEl);

  const isDraft = activity.status === SubmissionReviewStatus.Draft;
  const isLive = activity.submissionType === "LIV";
  const isSubmissionsOpen =
    activity.status === SubmissionReviewStatus.SubmissionsOpen;
  const isReviewsOpen = activity.status === SubmissionReviewStatus.ReviewsOpen;
  const isReviewsClosed =
    activity.status === SubmissionReviewStatus.ReviewsClosed;
  const canRemind = isSubmissionsOpen || isReviewsOpen;
  const canDelete = isDraft;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeader
        action={
          <>
            <SubmissionReviewState sx={{ ml: 2 }} status={activity.status} />
            <IconButton
              aria-label="more"
              aria-controls={open ? "activity-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              <MoreVert />
            </IconButton>
          </>
        }
        title={
          // Always reserve two lines: single-line titles pad to the same
          // height, longer titles clamp with an ellipsis. The full name is
          // available on hover.
          <Tooltip title={activity.name}>
            <Typography
              component={Link}
              to={`/submissions/${activity.id}`}
              variant="h5"
              sx={{
                color: "inherit",
                textDecoration: "none",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.3,
                height: "2.6em",
              }}
            >
              {activity.name}
            </Typography>
          </Tooltip>
        }
        subheader={
          <>
            {activity.rubricName ??
              activity.rubric?.name ??
              String(t("No rubric"))}
            <br />
            {new Date(activity.created).toLocaleString()}
          </>
        }
      />
      <Menu
        id="activity-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem component={Link} to={`/submissions/${activity.id}`}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          {t("View Activity")}
        </MenuItem>

        <Divider />

        {isSubmissionsOpen && (
          <MenuItem onClick={() => onChange(activity, "Close Submissions")}>
            <ListItemIcon>
              <Close fontSize="small" />
            </ListItemIcon>
            {t("Close Submissions")}
          </MenuItem>
        )}

        {isReviewsOpen && (
          <MenuItem onClick={() => onChange(activity, "Close Reviews")}>
            <ListItemIcon>
              <Close fontSize="small" />
            </ListItemIcon>
            {t("Close Reviews")}
          </MenuItem>
        )}

        {isReviewsClosed && (
          <MenuItem onClick={() => onChange(activity, "Release Results")}>
            <ListItemIcon>
              <Preview fontSize="small" />
            </ListItemIcon>
            {t("Release Results")}
          </MenuItem>
        )}

        {canRemind && (
          <MenuItem onClick={() => onRemind(activity)}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            {t("Send Reminder")}
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={() => onDelete(activity)}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            {t("Delete")}
          </MenuItem>
        )}
      </Menu>

      {/* Both rows are always rendered (placeholder before their stage) so
          every card has the same height regardless of state. */}
      <CardContent sx={{ mt: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <ProgressRow
            label={String(t(isLive ? "Presentations" : "Submissions"))}
            submitted={activity.submittedCount ?? 0}
            total={activity.submissionCount ?? 0}
          />
          <ProgressRow
            label={String(t("Reviews"))}
            submitted={activity.completedReviews ?? 0}
            total={activity.totalReviews ?? 0}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
